import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, ChevronRight, ChevronLeft, Loader2, X, Shield, Clock, Phone } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CITIES = [
  'Fougères',
  'Rennes',
  'Vitré',
  'Dol-de-Bretagne',
  'Pleine-Fougères',
  'Autre'
];

const PROPERTY_TYPES = [
  'Appartement',
  'Maison',
  'Longère',
  'Gîte',
  'Chambre d\'hôte',
  'Autre'
];

const AuditForm = ({ onSuccess }) => {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [formData, setFormData] = useState({
    nom: '',
    email: '',
    telephone: '',
    ville_bien: '',
    type_bien: '',
    nombre_chambres: 1,
    deja_en_location: null, // null = pas encore répondu, true/false = répondu
    rentabilite_annuelle: '',
    message: ''
  });

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const nextStep = () => {
    if (validateStep()) {
      setStep(prev => Math.min(prev + 1, 3));
    }
  };

  const prevStep = () => {
    setStep(prev => Math.max(prev - 1, 1));
  };

  const validateStep = () => {
    if (step === 1) {
      if (!formData.ville_bien || !formData.type_bien) {
        toast.error('Veuillez renseigner la ville et le type de bien');
        return false;
      }
    }
    if (step === 2) {
      if (!formData.nom || !formData.email || !formData.telephone) {
        toast.error('Veuillez renseigner vos coordonnées');
        return false;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        toast.error('Veuillez entrer un email valide');
        return false;
      }
    }
    if (step === 3) {
      if (formData.deja_en_location === null) {
        toast.error('Veuillez indiquer si votre bien est déjà en location');
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep()) return;

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/audit-requests`, {
        ...formData,
        deja_en_location: formData.deja_en_location === true
      });
      setShowSuccessModal(true);
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error('Une erreur est survenue. Veuillez réessayer.');
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      nom: '',
      email: '',
      telephone: '',
      ville_bien: '',
      type_bien: '',
      nombre_chambres: 1,
      deja_en_location: null,
      rentabilite_annuelle: '',
      message: ''
    });
    setStep(1);
    setShowSuccessModal(false);
  };

  const stepIndicators = [
    { num: 1, label: 'Votre bien' },
    { num: 2, label: 'Vos coordonnées' },
    { num: 3, label: 'Votre projet' }
  ];

  return (
    <>
      <div className="w-full max-w-xl mx-auto" data-testid="audit-form">
        {/* Step Indicators */}
        <div className="flex justify-between mb-12">
          {stepIndicators.map((s, index) => (
            <div key={s.num} className="flex items-center">
              <div className="flex flex-col items-center">
                <div 
                  className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-colors duration-300 ${
                    step > s.num 
                      ? 'border-[#C5A059] bg-[#C5A059] text-black' 
                      : step === s.num 
                        ? 'border-[#C5A059] text-[#C5A059] bg-[#C5A059]/10' 
                        : 'border-[#262626] text-[#A3A3A3]'
                  }`}
                >
                  {step > s.num ? <Check className="w-5 h-5" /> : s.num}
                </div>
                <span className={`mt-2 text-xs uppercase tracking-wider ${
                  step >= s.num ? 'text-[#C5A059]' : 'text-[#A3A3A3]'
                }`}>
                  {s.label}
                </span>
              </div>
              {index < stepIndicators.length - 1 && (
                <div className={`w-16 md:w-24 h-[2px] mx-2 mt-[-20px] ${
                  step > s.num ? 'bg-[#C5A059]' : 'bg-[#262626]'
                }`} />
              )}
            </div>
          ))}
        </div>

        <form onSubmit={handleSubmit}>
          <AnimatePresence mode="wait">
            {/* Step 1: Property Info */}
            {step === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="space-y-8"
              >
                <div>
                  <label className="block text-xs uppercase tracking-[0.2em] text-[#C5A059] mb-4">
                    Où se situe votre bien ?
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {CITIES.map((city) => (
                      <button
                        key={city}
                        type="button"
                        onClick={() => updateField('ville_bien', city)}
                        className={`py-3 px-4 border text-sm transition-colors duration-300 ${
                          formData.ville_bien === city
                            ? 'border-[#C5A059] bg-[#C5A059]/10 text-[#C5A059]'
                            : 'border-[#262626] text-[#A3A3A3] hover:border-[#C5A059]/50'
                        }`}
                        data-testid={`city-option-${city.toLowerCase().replace(/[^a-z]/g, '-')}`}
                      >
                        {city}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-xs uppercase tracking-[0.2em] text-[#C5A059] mb-4">
                    Type de bien
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {PROPERTY_TYPES.map((type) => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => updateField('type_bien', type)}
                        className={`py-3 px-4 border text-sm transition-colors duration-300 ${
                          formData.type_bien === type
                            ? 'border-[#C5A059] bg-[#C5A059]/10 text-[#C5A059]'
                            : 'border-[#262626] text-[#A3A3A3] hover:border-[#C5A059]/50'
                        }`}
                        data-testid={`type-option-${type.toLowerCase().replace(/[^a-z]/g, '-')}`}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-xs uppercase tracking-[0.2em] text-[#C5A059] mb-4">
                    Nombre de chambres
                  </label>
                  <div className="flex items-center gap-4">
                    <button
                      type="button"
                      onClick={() => updateField('nombre_chambres', Math.max(1, formData.nombre_chambres - 1))}
                      className="w-12 h-12 border border-[#262626] text-[#A3A3A3] hover:border-[#C5A059] hover:text-[#C5A059] transition-colors duration-300"
                      data-testid="btn-chambres-minus"
                    >
                      -
                    </button>
                    <span className="text-2xl font-playfair text-[#C5A059] w-12 text-center">
                      {formData.nombre_chambres}
                    </span>
                    <button
                      type="button"
                      onClick={() => updateField('nombre_chambres', formData.nombre_chambres + 1)}
                      className="w-12 h-12 border border-[#262626] text-[#A3A3A3] hover:border-[#C5A059] hover:text-[#C5A059] transition-colors duration-300"
                      data-testid="btn-chambres-plus"
                    >
                      +
                    </button>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 2: Contact Info */}
            {step === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="space-y-6"
              >
                <div>
                  <label className="block text-xs uppercase tracking-[0.2em] text-[#C5A059] mb-3">
                    Votre nom
                  </label>
                  <input
                    type="text"
                    value={formData.nom}
                    onChange={(e) => updateField('nom', e.target.value)}
                    className="w-full bg-transparent border-b border-[#262626] py-4 text-lg text-[#F5F5F5] placeholder:text-[#A3A3A3]/50 focus:border-[#C5A059] transition-colors duration-300 outline-none"
                    placeholder="Jean Dupont"
                    data-testid="input-nom"
                  />
                </div>

                <div>
                  <label className="block text-xs uppercase tracking-[0.2em] text-[#C5A059] mb-3">
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => updateField('email', e.target.value)}
                    className="w-full bg-transparent border-b border-[#262626] py-4 text-lg text-[#F5F5F5] placeholder:text-[#A3A3A3]/50 focus:border-[#C5A059] transition-colors duration-300 outline-none"
                    placeholder="jean@exemple.fr"
                    data-testid="input-email"
                  />
                </div>

                <div>
                  <label className="block text-xs uppercase tracking-[0.2em] text-[#C5A059] mb-3">
                    Téléphone
                  </label>
                  <input
                    type="tel"
                    value={formData.telephone}
                    onChange={(e) => updateField('telephone', e.target.value)}
                    className="w-full bg-transparent border-b border-[#262626] py-4 text-lg text-[#F5F5F5] placeholder:text-[#A3A3A3]/50 focus:border-[#C5A059] transition-colors duration-300 outline-none"
                    placeholder="06 12 34 56 78"
                    data-testid="input-telephone"
                  />
                </div>
              </motion.div>
            )}

            {/* Step 3: Project Details */}
            {step === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="space-y-8"
              >
                <div>
                  <label className="block text-xs uppercase tracking-[0.2em] text-[#C5A059] mb-4">
                    Votre bien est-il déjà en location ?
                  </label>
                  <div className="flex gap-4">
                    <button
                      type="button"
                      onClick={() => updateField('deja_en_location', true)}
                      className={`flex-1 py-4 border text-sm transition-colors duration-300 ${
                        formData.deja_en_location === true
                          ? 'border-[#C5A059] bg-[#C5A059]/10 text-[#C5A059]'
                          : 'border-[#262626] text-[#A3A3A3] hover:border-[#C5A059]/50'
                      }`}
                      data-testid="btn-deja-location-oui"
                    >
                      Oui, déjà en location
                    </button>
                    <button
                      type="button"
                      onClick={() => updateField('deja_en_location', false)}
                      className={`flex-1 py-4 border text-sm transition-colors duration-300 ${
                        formData.deja_en_location === false
                          ? 'border-[#C5A059] bg-[#C5A059]/10 text-[#C5A059]'
                          : 'border-[#262626] text-[#A3A3A3] hover:border-[#C5A059]/50'
                      }`}
                      data-testid="btn-deja-location-non"
                    >
                      Non, pas encore
                    </button>
                  </div>
                </div>

                <AnimatePresence>
                  {formData.deja_en_location === true && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                    >
                      <label className="block text-xs uppercase tracking-[0.2em] text-[#C5A059] mb-3">
                        Rentabilité annuelle actuelle (optionnel)
                      </label>
                      <input
                        type="text"
                        value={formData.rentabilite_annuelle}
                        onChange={(e) => updateField('rentabilite_annuelle', e.target.value)}
                        className="w-full bg-transparent border-b border-[#262626] py-4 text-lg text-[#F5F5F5] placeholder:text-[#A3A3A3]/50 focus:border-[#C5A059] transition-colors duration-300 outline-none"
                        placeholder="ex: 45 000 €"
                        data-testid="input-rentabilite"
                      />
                    </motion.div>
                  )}
                </AnimatePresence>

                <div>
                  <label className="block text-xs uppercase tracking-[0.2em] text-[#C5A059] mb-3">
                    Parlez-nous de votre projet
                  </label>
                  <textarea
                    value={formData.message}
                    onChange={(e) => updateField('message', e.target.value)}
                    rows={4}
                    className="w-full bg-transparent border border-[#262626] p-4 text-lg text-[#F5F5F5] placeholder:text-[#A3A3A3]/50 focus:border-[#C5A059] transition-colors duration-300 outline-none resize-none"
                    placeholder="Décrivez votre bien, vos attentes, vos questions..."
                    data-testid="input-message"
                  />
                </div>

                {/* Insurance Notice */}
                <div className="bg-[#C5A059]/5 border border-[#C5A059]/20 p-4 rounded-sm">
                  <div className="flex items-start gap-3">
                    <Shield className="w-5 h-5 text-[#C5A059] mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-[#F5F5F5] font-medium mb-1">
                        Assurance obligatoire
                      </p>
                      <p className="text-xs text-[#A3A3A3] leading-relaxed">
                        Pour confier le pilotage de votre bien à Confort Hôte, une assurance spécifique 
                        à l'activité de location courte durée est obligatoire. Nous vous accompagnons 
                        dans cette démarche.
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-12">
            {step > 1 ? (
              <button
                type="button"
                onClick={prevStep}
                className="flex items-center gap-2 text-[#A3A3A3] hover:text-[#C5A059] transition-colors duration-300"
                data-testid="btn-prev-step"
              >
                <ChevronLeft className="w-4 h-4" />
                <span className="text-sm uppercase tracking-wider">Retour</span>
              </button>
            ) : (
              <div />
            )}

            {step < 3 ? (
              <button
                type="button"
                onClick={nextStep}
                className="flex items-center gap-2 bg-[#C5A059] text-black px-8 py-4 text-sm uppercase tracking-[0.15em] font-semibold hover:bg-[#D4AF37] transition-colors duration-300"
                data-testid="btn-next-step"
              >
                <span>Continuer</span>
                <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex items-center gap-2 bg-[#C5A059] text-black px-8 py-4 text-sm uppercase tracking-[0.15em] font-semibold hover:bg-[#D4AF37] transition-colors duration-300 disabled:opacity-50"
                data-testid="btn-submit-audit"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Envoi...</span>
                  </>
                ) : (
                  <span>Envoyer ma demande</span>
                )}
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Success Modal */}
      <Dialog open={showSuccessModal} onOpenChange={setShowSuccessModal}>
        <DialogContent className="bg-[#0A0A0A] border border-[#262626] max-w-md">
          <DialogHeader>
            <DialogTitle className="sr-only">Demande envoyée</DialogTitle>
          </DialogHeader>
          <div className="text-center py-6">
            <div className="w-16 h-16 bg-[#C5A059]/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-8 h-8 text-[#C5A059]" />
            </div>
            <h3 className="text-2xl font-playfair text-[#F5F5F5] mb-4">
              Merci pour votre confiance !
            </h3>
            <p className="text-[#A3A3A3] font-light leading-relaxed mb-6">
              Votre demande d'audit a bien été enregistrée. Notre équipe vous recontactera 
              dans les <span className="text-[#C5A059] font-medium">24 heures</span> pour 
              échanger sur votre projet.
            </p>
            
            <div className="bg-[#121212] border border-[#262626] p-4 mb-6">
              <div className="flex items-center justify-center gap-6">
                <div className="flex items-center gap-2 text-[#A3A3A3]">
                  <Clock className="w-4 h-4 text-[#C5A059]" />
                  <span className="text-sm">Réponse sous 24h</span>
                </div>
                <div className="flex items-center gap-2 text-[#A3A3A3]">
                  <Phone className="w-4 h-4 text-[#C5A059]" />
                  <span className="text-sm">Appel personnalisé</span>
                </div>
              </div>
            </div>

            <p className="text-sm text-[#A3A3A3] mb-6">
              À très bientôt,<br/>
              <span className="text-[#C5A059]">L'équipe Confort Hôte</span>
            </p>

            <button
              onClick={resetForm}
              className="bg-[#C5A059] text-black px-8 py-3 text-sm uppercase tracking-[0.15em] font-semibold hover:bg-[#D4AF37] transition-colors duration-300"
              data-testid="btn-close-success"
            >
              Fermer
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AuditForm;
